import { useCallback } from 'react';
import { useSpaceContext } from '../contexts/SpaceContext';
import { useAuth } from '../store/auth-context';
import { meetsRole } from '../lib/space-roles';

/**
 * Client-side mirror of the server's entity gates. Rendering decisions only —
 * the API enforces the same rules, so a stale role here can never grant
 * anything.
 *
 * - `canEdit`   — Member+ may create and update Space entities.
 * - `canDelete` — Admin+ may delete anyone's entity; the creator may delete
 *                 their own from Member up. Mirrors `requireRoleOrOwner` in
 *                 optr-agent-core (`src/lambda/utils.ts`), including the
 *                 Member floor: a creator demoted to Viewer loses the
 *                 carve-out.
 */
export function useEntityPermissions() {
  const { role } = useSpaceContext();
  const { user } = useAuth();

  const canDelete = useCallback(
    (entity: { userId?: string } | null | undefined): boolean => {
      if (!entity) return false;
      const isCreator = Boolean(entity.userId) && entity.userId === user?.userId;
      return meetsRole(role, isCreator ? 'member' : 'admin');
    },
    [role, user?.userId],
  );

  return {
    canEdit: meetsRole(role, 'member'),
    canDelete,
  };
}
